import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import type { ExploitDbExtraction } from '@intelhub/shared'

const PREVIEW_LINES = 30

/**
 * Reader pane for exploit-db.com articles.
 *
 * Renders structured metadata (CVE, type, platform, author, EDB-ID link, verified
 * badge) plus a preview of the exploit code block (first 30 lines, expandable).
 *
 * Selected by `ArticleReaderPane` when the article carries a populated
 * `fullTextMeta` payload (see `apps/pipeline/src/processing/extract-exploit-db.ts`).
 */
export function ExploitDbReaderPane({
  meta,
  url,
  onContinueToSource,
}: {
  meta: ExploitDbExtraction
  url: string
  onContinueToSource: (url: string) => void
}) {
  const [expanded, setExpanded] = useState(false)

  const codeLines = meta.code === '' ? [] : meta.code.split('\n')
  const visibleLines = expanded ? codeLines : codeLines.slice(0, PREVIEW_LINES)
  const hasMore = codeLines.length > PREVIEW_LINES

  return (
    <div
      className="rounded-md border bg-muted/30 p-4 space-y-3"
      data-testid="exploit-db-reader-pane"
    >
      {/* Metadata strip */}
      <div className="flex flex-wrap gap-1.5 items-center text-xs">
        {meta.cveIds.map((cve) => (
          <Badge key={cve} variant="secondary">
            {cve}
          </Badge>
        ))}
        <Badge variant="outline">{meta.exploitType}</Badge>
        <Badge variant="outline">{meta.platform}</Badge>
        <span className="text-muted-foreground">{meta.author}</span>
        <a
          href={`https://www.exploit-db.com/exploits/${meta.edbId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline-offset-2 hover:underline"
        >
          EDB-{meta.edbId}
        </a>
        {meta.verified && (
          <Badge variant="secondary" className="text-green-600">
            ✓ verified
          </Badge>
        )}
      </div>

      {/* Code block */}
      {meta.code === '' ? (
        <p className="text-sm text-muted-foreground">No code block found on this page.</p>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-muted-foreground font-mono">
              {meta.codeLanguage}
            </span>
            <span className="text-xs text-muted-foreground">
              {meta.codeLineCount} lines
            </span>
          </div>
          <pre
            data-testid="code-block"
            className="bg-muted/50 rounded p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed"
          >
            {visibleLines.join('\n')}
          </pre>
          {hasMore && (
            <button
              type="button"
              className="mt-1.5 text-xs text-primary underline-offset-2 hover:underline"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Collapse ↑' : `Show all ${meta.codeLineCount} lines ↓`}
            </button>
          )}
        </div>
      )}

      {/* Continue to source */}
      <p className="text-xs">
        <button
          type="button"
          className="text-primary underline-offset-2 hover:underline"
          onClick={() => onContinueToSource(url)}
        >
          Continue to source ↗
        </button>
      </p>
    </div>
  )
}
